import { useState } from 'react';
import { Card, Input, Kpi, KpiStrip, Pill, SegmentedControl, TableFooter, TableHead, TableRow, type PillTone } from '@os/ui';
import { useAudit } from '../../api';
import { Page, PageAlert, PageEmpty } from '../../desktop/Page';
import './admin.css';

type Kind = 'all' | 'signin' | 'security' | 'failed';
const COLS = '170px 190px minmax(0, 1fr) 130px';

/** login.* / logout / identity.* are sign-in events; *.failed are failures; everything else changes security state. */
const kindOf = (action: string): Exclude<Kind, 'all'> =>
  action.endsWith('.failed') ? 'failed' : action.startsWith('login') || action === 'logout' || action.startsWith('identity') ? 'signin' : 'security';
const tone = (action: string): PillTone => (kindOf(action) === 'failed' ? 'danger' : action.startsWith('mfa') || action.startsWith('password') ? 'warn' : 'outline');
const fmt = (iso: string) => new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** Audit log: KPI strip, kind tabs + search, full-width event table. */
export default function Audit() {
  const { data, error } = useAudit();
  const [kind, setKind] = useState<Kind>('all');
  const [q, setQ] = useState('');
  const list = data ?? [];
  const failed = list.filter((e) => kindOf(e.action) === 'failed');
  const security = list.filter((e) => kindOf(e.action) === 'security');
  const signin = list.filter((e) => kindOf(e.action) === 'signin');
  const ips = new Set(list.map((e) => e.ip).filter(Boolean)).size;
  const needle = q.trim().toLowerCase();
  const details = (e: (typeof list)[number]) => (e.data && typeof e.data === 'object' && Object.keys(e.data).length ? JSON.stringify(e.data) : '');
  const shown = list
    .filter((e) => kind === 'all' || kindOf(e.action) === kind)
    .filter((e) => !needle || `${e.action} ${e.target_type ?? ''} ${e.target_id ?? ''} ${e.ip ?? ''} ${details(e)}`.toLowerCase().includes(needle));

  return (
    <Page title="Audit log" subtitle="Security-relevant events across the OS, newest first">
      <PageAlert error={error} fallback="Could not load the audit log" />

      <KpiStrip columns={4}>
        <Kpi label="Events" value={data ? list.length : '…'} sub="Most recent 200" />
        <Kpi label="Failed attempts" value={data ? failed.length : '…'} tone={failed.length > 0 ? 'danger' : 'muted'} />
        <Kpi label="Security changes" value={data ? security.length : '…'} sub="Users, roles, MFA, passwords, apps" />
        <Kpi label="Distinct IPs" value={data ? ips : '…'} sub={list[0] ? `Last event ${fmt(list[0].created_at)}` : undefined} />
      </KpiStrip>

      <Card>
        <div className="pg-toolbar">
          <SegmentedControl variant="tabs" value={kind} onChange={setKind} segments={[
            { value: 'all', label: 'All', count: list.length },
            { value: 'signin', label: 'Sign-in', count: signin.length },
            { value: 'security', label: 'Security', count: security.length },
            { value: 'failed', label: 'Failures', count: failed.length },
          ]} />
          <Input compact className="pg-search" placeholder="Search action, target, IP" aria-label="Search audit log" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="pg-scroll">
          <div>
            <TableHead cols={COLS} columns={[{ label: 'When' }, { label: 'Action' }, { label: 'Target · details' }, { label: 'IP', align: 'right' }]} />
            {shown.map((e) => (
              <TableRow key={e.id} cols={COLS}>
                <span className="meta-sm tabular">{fmt(e.created_at)}</span>
                <span><Pill small tone={tone(e.action)}>{e.action}</Pill></span>
                <span className="truncate" title={details(e)}>
                  {e.target_type && <span className="aud-target">{e.target_type} {e.target_id ?? ''}</span>}
                  {e.target_type && details(e) && ' '}
                  {details(e) && <span className="aud-data">{details(e)}</span>}
                  {!e.target_type && !details(e) && <span className="meta-sm">—</span>}
                </span>
                <span className="meta-sm tabular" style={{ textAlign: 'right' }}>{e.ip ?? '—'}</span>
              </TableRow>
            ))}
            {data && shown.length === 0 && <PageEmpty>{list.length === 0 ? 'Nothing recorded yet.' : 'No events match this filter.'}</PageEmpty>}
          </div>
        </div>
        <TableFooter><span>{shown.length} of {list.length} events</span></TableFooter>
      </Card>
    </Page>
  );
}
